import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import InputWrapper from "../inputWrapper";
import Button from "../button";
import {
  login,
  hideModal,
  postRequest,
  getUserProfile,
  fetchExperiences,
  showSignUpModal,
  showMentorSignUpModal,
  getMentorById,
} from "../../redux/action";
import styles from "./auth.module.css";
import { logoWhiteImg, whiteEyeIcon, whiteCloseEyeIcon } from "../../images";
import { mergeGuestCards } from "../../utils/installmentPayments";
import { clearScopedJwt } from "../../utils/scopedAuth";
import {
  resolveOriginalRegisteredRole,
  storeOriginalRegisteredRole,
} from "../../utils/originalRegisteredRole";

const isMentorAccount = (user) =>
  Boolean(user?.isMentor || user?.role === "mentor" || user?.mentor);

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const storedAuthVariant = useSelector(
    (state) => state.authVariant || "student",
  );
  const authVariant = location.state?.authVariant || storedAuthVariant;
  const isMentorLogin = authVariant === "mentor";
  const loginTitle = isMentorLogin ? "Mentor Log In" : "Student Log In";
  const loginSubtitle = isMentorLogin
    ? "Access your mentor dashboard, sessions, and availability."
    : "Access your courses, bookings, and dashboard.";
  const signUpLabel = isMentorLogin
    ? "Sign up as a mentor"
    : "Sign up as a student";

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isValid },
  } = useForm({ mode: "all" });

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSignUpClick = () => {
    dispatch(hideModal());
    if (isMentorLogin) {
      dispatch(showMentorSignUpModal());
    } else {
      dispatch(showSignUpModal());
    }
  };

  const expirationTimeInHours = 24;
  const expirationDate = new Date(
    new Date().getTime() + expirationTimeInHours * 60 * 60 * 1000,
  );
  const onSubmit = async (data) => {
    const formData = {
      identifier: data.email,
      password: data.password,
    };
    try {
      setLoading(true);
      const response = await postRequest("/api/auth/local", formData);
      const responseData = response[1];

      if (responseData?.error?.message) {
        setLoading(false);
        const errorMsg = responseData.error.message;
        // Set field-specific errors
        setError("email", { type: "server", message: errorMsg });
        toast.error(errorMsg, { autoClose: 2000 });
      } else {
        setLoading(false);
        const { jwt, user } = responseData;
        const isMentor = isMentorAccount(user);

        if (user?.confirmed) {
          // UNIFIED ACCOUNT: Allow dual-role users to log in with either variant
          // Only block if a student tries mentor login without any mentor profile
          if (isMentorLogin && !isMentor) {
            const hasMentorProfile = user?.experiences?.mentor?.hasProfile;
            if (!hasMentorProfile) {
              const errorMsg = "This email is not registered as a mentor. Please sign up as a mentor first.";
              setError("email", { type: "server", message: errorMsg });
              toast.error(errorMsg, { autoClose: 3000 });
              return;
            }
          }

          Cookies.set("token", jwt, {
            expires: expirationDate,
          });

          clearScopedJwt();

          reset();

          const originalRegisteredRole = resolveOriginalRegisteredRole(user);
          storeOriginalRegisteredRole(user.email, originalRegisteredRole);
          const loggedInUser = {
            ...user,
            originalRegisteredRole,
            token: jwt,
          };

          dispatch(login(loggedInUser));

          // Fetch full profile including enrolled courses
          dispatch(getUserProfile(jwt));

          // UNIFIED ACCOUNT: Fetch experiences state
          dispatch(fetchExperiences(jwt));

          // IMPORTANT: Merge guest cards after login
          try {
            await mergeGuestCards(jwt, user.email);
          } catch (mergeError) {
            console.error("Failed to merge guest cards:", mergeError);
            // Don't block login flow if merge fails
          }

          dispatch(hideModal());

          toast.success("Successfully logged in!", { autoClose: 2000 });

          // UNIFIED ACCOUNT: Route based on which login form was used
          if (isMentorLogin) {
            if (user?.mentor?.id) {
              dispatch(getMentorById(user.mentor.id))
                .then(() => navigate("/mentor-profile/me"))
                .catch((err) => {
                  console.error("Failed to fetch mentor data:", err);
                  navigate("/mentor-profile/me");
                });
            } else {
              navigate("/mentor-profile/me");
            }
          } else {
            navigate("/dashboard");
          }
        } else {
          const errorMsg = "Wrong email or password";
          setError("email", { type: "server", message: errorMsg });
          setError("password", { type: "server", message: errorMsg });
          toast.error(errorMsg, { autoClose: 2000 });
        }
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
      toast.error(`An error occurred submitting your info`, {
        autoClose: 2000,
      });
    }
  };

  return (
    <section>
      <div className={styles["authContentModal"]}>
        <Link to={"/"} className={styles["imageWrapper"]}>
          <img
            src={logoWhiteImg}
            alt="logo"
            onClick={() => dispatch(hideModal())}
          />
        </Link>
        <h2 className={styles["authHeader"]}>{loginTitle}</h2>
        <p className={styles["authSubTitle"]}>{loginSubtitle}</p>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={styles["formWrapper"]}
        >
          <div className="w-full">
            <InputWrapper
              title="Email"
              type="email"
              placeholder="Example@example.com"
              fieldName="email"
              requiredMessage="Email is required"
              patternMessage="Invalid email address"
              register={register}
              errors={errors}
            />
            <InputWrapper
              inputTag="Password"
              title="Password"
              type={showPassword ? "text" : "password"}
              fieldName="password"
              requiredMessage="Password is required"
              register={register}
              errors={errors}
              icon={showPassword ? whiteEyeIcon : whiteCloseEyeIcon}
              onImageClick={handleShowPassword}
              className={"!text-[14px]"}
              url={"/reset-password"}
              inputLink={"Forgot Password"}
            />
          </div>
          <Button
            type="submit"
            variant={isValid ? "secondary" : "disabled"}
            size="full"
            disabled={!isValid}
            loading={loading}
          >
            Log in
          </Button>
        </form>
        <div className={styles["accountTextWrapper"]}>
          <p className={styles["accountText"]}>
            Don't have a {isMentorLogin ? "mentor" : "student"} account?
          </p>
          <Link
            to="/signup"
            onClick={handleSignUpClick}
            className={`${styles["accountText"]} ${styles["signUpText"]}`}
          >
            {signUpLabel}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Login;
